/**
 * Open / close / toggle — the ONE way to show the palette (shortcut, ⌘P,
 * title-bar pill, Documents' search hint). The opener is captured here,
 * before the palette takes focus; `closePalette({ restoreFocus })` gives it
 * back unless the close was a navigation (Enter on a result).
 */
import { useUiModalStore } from '../../store/uiModalStore';
import { captureOpener, restoreOpener } from './focusReturn';
import { markStart } from '../../palette/metrics';

let opener = null;

export function isPaletteOpen() {
  return !!useUiModalStore.getState().showCommandPalette;
}

/**
 * @param {{ mode?: 'quickOpen'|null, chip?: string|null, at?: number }} [opts]
 *   chip: a provider id to start scoped in; default: Documents pre-sets `docs:` (D2).
 */
export function openPalette({ mode = null, chip, at } = {}) {
  const s = useUiModalStore.getState();
  if (s.showCommandPalette) return;
  opener = captureOpener();
  const initialChip = chip !== undefined ? chip : s.activeTab === 'documents.panel' && mode !== 'quickOpen' ? 'documents' : null;
  s.setCommandPaletteFilter(initialChip);
  s.setCommandPaletteMode(mode);
  markStart('open', 'o', at);
  s.setShowCommandPalette(true);
}

export function closePalette({ restoreFocus = true } = {}) {
  const s = useUiModalStore.getState();
  const info = opener;
  opener = null;
  if (!s.showCommandPalette) return;
  s.setShowCommandPalette(false);
  s.setCommandPaletteFilter(null);
  s.setCommandPaletteMode(null);
  if (restoreFocus && info) {
    // At once: the popover palette does not make the page inert, so the
    // opener can take focus back while the close is still being committed.
    // Waiting (a frame, or even a 0 ms task) let the first key typed right
    // after Esc land in the palette's input — the terminal got "echo MAK"
    // for "echo MARK" in the stack e2e. The deferred passes cover the
    // <dialog> fallback (showModal makes the page inert until it closes),
    // add the selection and the disconnected-opener fallback.
    // A deferred pass never runs once the palette is open again (⌘K pressed
    // within that frame): it would pull focus out of the new palette's input,
    // and the keys typed next would land in the page underneath.
    let done = false;
    const run = () => {
      if (done && (document.activeElement === info.el || isPaletteOpen())) return;
      done = true;
      restoreOpener(info);
    };
    run();
    setTimeout(run, 0);
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  }
}

export function togglePalette(opts = {}) {
  if (isPaletteOpen()) closePalette({ restoreFocus: true });
  else openPalette(opts);
}

/** Tests. */
export function peekOpener() {
  return opener;
}
